import {
  AspectRatio,
  Box,
  Center,
  HStack,
  Image,
  StackProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import { IContestListItem } from "@/models/contestListItem";
import { resizeServingUrl } from "@/utils/image";
import NewIcon from "./assets/new-icon.svg";

type Props = {
  item: IContestListItem;
  size: "md" | "lg";
} & StackProps;

export const ContestListItem = ({ item, size, ...props }: Props) => {
  return (
    <VStack w="100%" gap="8px" {...props}>
      <Box w="full" position="relative">
        <AspectRatio
          ratio={361 / 203}
          borderRadius={8}
          overflow="hidden"
          w="100%"
        >
          {item.thumbnailUrl ? (
            <Image
              src={resizeServingUrl(item.thumbnailUrl, 360 * 2)!}
              w="full"
              h="full"
              objectFit="cover"
            />
          ) : (
            <Box w="full" h="full" bg="gray.300" />
          )}
        </AspectRatio>

        {shouldShowNew(item) && (
          <Center
            position="absolute"
            w="42px"
            h="24px"
            top="8px"
            left="8px"
            bg="contentNegative"
            borderRadius="4px"
          >
            <Image alt="NEW" src={NewIcon} w="30px" />
          </Center>
        )}
      </Box>

      <VStack w="full" gap="4px" alignItems="flex-start">
        <Text
          fontSize={size === "lg" ? 18 : 14}
          fontWeight="bold"
          color="contentPrimary"
        >
          {item.name}
        </Text>
        {size === "lg" && (
          <Text fontSize={14} color="contentSecondary" lineClamp={2}>
            {item.description}
          </Text>
        )}
      </VStack>

      <HStack w="full" gap="8px" alignItems="center" flex={1}>
        {item.status === "CONTEST_STATUS_DISCLOSED" ||
        item.status === "CONTEST_STATUS_OPENED" ||
        item.status === "CONTEST_STATUS_CLOSED" ||
        item.status === "CONTEST_STATUS_RESULT_ANNOUNCED" ? (
          <StatusLabel status={item.status} size={size} />
        ) : null}

        {item.openEntryAt || item.closeEntryAt ? (
          <Text
            fontSize={size === "lg" ? 14 : 10}
            color="contentPrimary"
            flexShrink={1}
          >
            {item.openEntryAt ? formatDate(item.openEntryAt) : ""} 〜{" "}
            {item.closeEntryAt ? formatDate(item.closeEntryAt) : ""}
          </Text>
        ) : null}
      </HStack>
    </VStack>
  );
};

const StatusLabel = ({
  status,
  size,
}: {
  status:
    | "CONTEST_STATUS_DISCLOSED"
    | "CONTEST_STATUS_OPENED"
    | "CONTEST_STATUS_CLOSED"
    | "CONTEST_STATUS_RESULT_ANNOUNCED";
  size: "lg" | "md";
}) => {
  const label =
    status === "CONTEST_STATUS_DISCLOSED"
      ? "開始前"
      : status === "CONTEST_STATUS_OPENED"
        ? "応募受付中"
        : status === "CONTEST_STATUS_CLOSED"
          ? "選考中"
          : status === "CONTEST_STATUS_RESULT_ANNOUNCED"
            ? "受付終了"
            : "";
  const color =
    status === "CONTEST_STATUS_DISCLOSED"
      ? "#F43F5E"
      : status === "CONTEST_STATUS_OPENED"
        ? "#15B8A5"
        : status === "CONTEST_STATUS_CLOSED"
          ? "#FFAE00"
          : status === "CONTEST_STATUS_RESULT_ANNOUNCED"
            ? "#989BA0"
            : undefined;

  return (
    <Center
      px="6px"
      py="4px"
      borderRadius={4}
      borderColor={color}
      borderWidth={1}
      flexShrink={0}
    >
      <Text fontSize={size === "lg" ? 12 : 10} color={color} fontWeight="bold">
        {label}
      </Text>
    </Center>
  );
};

const formatDate = (date: Date) => {
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
};

// 応募の開催が現在の日時より前もしくは、開始から14日以内の場合に新着アイコンを表示する
const shouldShowNew = ({ openEntryAt, status }: IContestListItem) => {
  // openEntryAt が存在しない場合は false を返す
  if (!openEntryAt) {
    return false;
  }
  // ステータスが告知中または応募受付中でない場合は false を返す
  if (
    !(
      status === "CONTEST_STATUS_DISCLOSED" ||
      status === "CONTEST_STATUS_OPENED"
    )
  ) {
    return false;
  }

  const now = new Date();
  const openEntryFourteenDaysLater =
    openEntryAt.getTime() + 14 * 24 * 60 * 60 * 1000; // 14日後のタイムスタンプを計算

  return (
    now.getTime() < openEntryAt.getTime() || // 応募開始前
    now.getTime() < openEntryFourteenDaysLater // 14日以内
  );
};
